import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep,
    get as _get,
} from 'lodash';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';

import Validator, { useValidate, checkValidity } from '../../../components/validator';

import SystemService from '../../../data/system-service';
import Auth from '../../../data/auth-service';
import DataContext from '../../../data/data-context';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    warning: {
        border: `1px solid ${theme.palette.error.main}`,
        background: theme.styled.paper.background,
        padding: theme.spacing(2),
    },
}));

const Proxy = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { systemSettings, updateSystemSettings } = useContext(DataContext);
    const [original, setOriginal] = useState({});
    const [proxy, setProxy] = useState({
        http_proxy: '',
        no_proxy: '',
    });
    const [changes, setChanges] = useState(false);
    const proxyForm = useValidate({});
    const [disableCheck, setDisableCheck] = useState(false);
    const [isSaml, setIsSaml] = useState(false);
    const proxyPerms = Auth.permissions.get('Proxy');

    useEffect(() => {
        if (systemSettings && systemSettings.proxy) {
            const p = _cloneDeep(systemSettings.proxy);
            setProxy(p);
            setOriginal(systemSettings.proxy);
        }
        setIsSaml(_get(systemSettings, 'auth.saml.enabled', false));
    }, [systemSettings]);

    useEffect(() => {
        setChanges(!_isEqual(proxy, original));
    }, [proxy, original]);

    const handleProxy = (e, validID, valid) => {
        proxyForm.set({ ...proxyForm.get(), [validID]: valid });

        setProxy(current => {
            return { ...current, http_proxy: e.target.value };
        });
    };

    const handleNoProxy = e => {
        setProxy(current => {
            return { ...current, no_proxy: e.target.value };
        });
    };

    const handleCheckProxy = () => {
        setDisableCheck(true);
        SystemService.checkProxyURL({
            checkMethod: 'check-proxy-url',
            error: '',
            help: 'Proxy URL for HTTP(s) requests.  Username, password, and port are optional.',
            label: '',
            placeholder: 'ex: http://user:passwd@example.com:8888',
            readwrite: true,
            url: proxy.http_proxy,
        }).then((res) => {
            toast.success('Proxy URL check successful');
            setDisableCheck(false);
        }, err => {
            setDisableCheck(false);
            if (err.error === 'Bad Request') {
                toast.error('Cannot establish a connection with the proxy');
            } else {
                toast.error(err.error);
            }
        });
    };

    const handleSave = () => {
        updateSystemSettings({ proxy });
    };

    const handleCancel = () => {
        navigate('/system/configuration');
    };

    return (
        <Grid container>
            <Grid item lg={6}>
                <Paper elevation={3}>
                    {isSaml && <Box className={classes.warning}>
                            This change requires the CMC login service to be restarted When SAML Authentication is used. This will occure almost
                            immediately after saving these changes and it may momentarily disrupt the UI service.
                    </Box>}
                    <Box p={5}>
                        {/*proxy with check button*/}
                        <Validator
                            disabled={!proxyPerms.write}
                            valueType="host"
                            allowEmpty
                            fullWidth
                            placeholder={'ex. http://user:passwd@example.com:8888'}
                            value={proxy.http_proxy}
                            onChange={handleProxy}
                            endAdornment={proxyPerms.write
                                ? <Button aria-label="check" onClick={handleCheckProxy} disabled={!proxy.http_proxy.length || disableCheck}>Check</Button>
                                : null
                            }
                        />
                        <FormHelperText>Proxy URL for HTTP(s) requests. Username, password, and port are optional</FormHelperText>

                        {/*no proxy - no validation and no test connection*/}
                        <TextField
                            style={{ marginTop: 20 }}
                            disabled={!proxyPerms.write || !_get(proxy, 'http_proxy')}
                            fullWidth
                            placeholder={'ex. localhost,127,0.0/8,::1'}
                            value={proxy.no_proxy || ''}
                            onChange={handleNoProxy}
                        />
                        <FormHelperText>List of URLs or domains to skip proxying</FormHelperText>

                        {proxyPerms.write && <Box mt={3} mb={5}>
                            <Button
                                className={classes.buttonRight}
                                variant="contained"
                                color="primary"
                                aria-label="save"
                                onClick={handleSave}
                                disabled={!proxyPerms.write || !changes || (!!_get(proxy, 'http_proxy') && !checkValidity(proxyForm.get()))}
                            >Save</Button>
                            <Button className={classes.buttonRight} variant="contained" color="secondary" aria-label="cancel" onClick={handleCancel}>Cancel</Button>
                        </Box>}
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default Proxy;
